/**
 * Translation servis (TASK 30) — OpenAI proxy klijent.
 *
 * Server: https://openai-translate.7-all.com/v1/translate
 * Server-side cache: 7 dana TTL po SHA256 hash-u
 * Frontend cache: sessionStorage (nestaje pri zatvaranju taba)
 *
 * Migracija sa starog 7GCOM klijenta (https://7gcom.7-all.com/v1/translate/text):
 *   - novi argumenti: (text, sourceLang='bs', targetLang)  — redoslijed obrnut!
 *   - cijeli markdown content ide u jednom API pozivu (OpenAI razumije Markdown
 *     strukturu i čuva formatting; više nema paragraph-by-paragraph splitanja)
 */

const TRANSLATE_URL = 'https://openai-translate.7-all.com/v1/translate';
const CACHE_PREFIX = 'suton:translate:v2:';

const LANG_MAP: Record<string, string> = {
  bs: 'bs',
  hr: 'hr',
  sr: 'sr',
  'sr-Cyrl': 'sr-Cyrl',
  mk: 'mk',
  de: 'de',
  en: 'en',
};

/** Normalizuj locale: en-US → en, de-DE → de; izuzetak sr-Cyrl ostaje pun. */
export function normalizeLang(lang: string): string {
  if (!lang) return lang;
  if (lang === 'sr-Cyrl' || lang.toLowerCase() === 'sr-cyrl') return 'sr-Cyrl';
  return lang.split('-')[0];
}

function cacheKey(text: string, sourceLang: string, targetLang: string): string {
  // Skraćeni hash za sessionStorage key (text može biti dugačak, btoa to handluje)
  const hash = btoa(unescape(encodeURIComponent(`${sourceLang}|${targetLang}|${text}`)))
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 64);
  return `${CACHE_PREFIX}${hash}`;
}

/**
 * Prevodi tekst sa `sourceLang` (default `bs`) na `targetLang`.
 * Vraća original ako je target = source ili ako API ne odgovori.
 *
 * NOTE: Argument `targetLang` JE drugi pozitivni parametar (pošto `sourceLang`
 * ima default `'bs'`). Pozivanje: `translateText('text', 'bs', 'de')`.
 */
export async function translateText(
  text: string,
  sourceLang: string = 'bs',
  targetLang?: string
): Promise<string> {
  if (!text || !text.trim()) return text;
  if (!targetLang) return text;

  const normSource = normalizeLang(sourceLang);
  const normTarget = normalizeLang(targetLang);
  const mappedSource = LANG_MAP[normSource] || LANG_MAP[sourceLang] || normSource;
  const mappedTarget = LANG_MAP[normTarget] || LANG_MAP[targetLang] || normTarget;
  if (mappedSource === mappedTarget) return text;

  // 1. Frontend cache (sessionStorage)
  const key = cacheKey(text, mappedSource, mappedTarget);
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) return cached;
  } catch {
    // sessionStorage nije dostupan (private mode itd.)
  }

  // 2. API poziv na OpenAI proxy
  try {
    const res = await fetch(TRANSLATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        sourceLang: mappedSource,
        targetLang: mappedTarget,
      }),
    });

    if (!res.ok) {
      console.warn('[translateText] HTTP', res.status, '— fallback na original');
      return text;
    }

    const data = await res.json();
    const translation: string =
      (typeof data?.translation === 'string' && data.translation) ||
      (typeof data?.translated_text === 'string' && data.translated_text) ||
      text;

    try {
      sessionStorage.setItem(key, translation);
    } catch {
      // quota / privacy — ignoriraj
    }

    return translation;
  } catch (err) {
    console.warn('[translateText] error:', err);
    return text;
  }
}

/** Paralelni prevod više stringova (zadržava redoslijed). */
export async function translateBatch(
  texts: string[],
  sourceLang: string = 'bs',
  targetLang?: string
): Promise<string[]> {
  return Promise.all(texts.map((t) => translateText(t, sourceLang, targetLang)));
}

/** Briše sve translation cache entry-je iz sessionStorage. */
export function clearTranslationCache(): void {
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach((k) => {
      if (k.startsWith(CACHE_PREFIX)) {
        sessionStorage.removeItem(k);
      }
    });
  } catch {
    // ignore
  }
}
